'use client';

import { useState } from 'react';
import { MODAL_OVERLAY, MODAL_PANEL } from '@/components/ui/modal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bell, X, Check, Loader2 } from 'lucide-react';

interface PriceAlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  origin: string;
  destination: string;
  originName: string;
  destinationName: string;
  onSuccess?: () => void;
}

export function PriceAlertModal({
  isOpen,
  onClose,
  origin,
  destination,
  originName,
  destinationName,
  onSuccess,
}: PriceAlertModalProps) {
  const [targetPrice, setTargetPrice] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<number | null>(null);

  if (!isOpen) return null;

  const handleClose = () => {
    setTargetPrice('');
    setError(null);
    setSuccess(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const parsed = parseInt(targetPrice, 10);
    if (isNaN(parsed) || parsed < 100) {
      setError('Geçerli bir fiyat girin (minimum 100 TL)');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch('/api/price-alerts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin, destination, targetPrice: parsed }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data?.error || 'Bir hata oluştu, tekrar deneyin');
        return;
      }

      setSuccess(parsed);
      onSuccess?.();
    } catch {
      setError('Bağlantı hatası, tekrar deneyin');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[100] backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className={`${MODAL_OVERLAY} pointer-events-none`}>
        <Card className={`${MODAL_PANEL} max-w-md pointer-events-auto animate-in zoom-in-95 duration-200`}>
          <CardHeader className="relative">
            <button
              onClick={handleClose}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mx-auto mb-4">
              <Bell className="w-8 h-8 text-primary-600" />
            </div>

            <CardTitle className="text-center text-2xl">
              Hedef Fiyat Belirle
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-6">
            {success !== null ? (
              <>
                <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-green-800">
                    Alarm kuruldu! 🔔 {success} TL'nin altına düşünce size haber vereceğiz.
                  </p>
                </div>
                <Button onClick={handleClose} className="w-full" size="lg">
                  Kapat
                </Button>
              </>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <p className="text-center text-gray-700">
                  {originName} → {destinationName} rotası için bir hedef fiyat belirleyin.
                  Fiyat bu değerin altına düştüğünde size bir kez e-posta göndereceğiz.
                </p>

                <div>
                  <label htmlFor="targetPrice" className="block text-sm font-medium text-gray-700 mb-1">
                    Hedef Fiyat (TL)
                  </label>
                  <input
                    type="number"
                    id="targetPrice"
                    value={targetPrice}
                    onChange={(e) => setTargetPrice(e.target.value)}
                    placeholder="1500"
                    min="100"
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 transition-shadow focus:ring-2 focus:ring-primary-500 focus:border-transparent focus:shadow-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Bu fiyatın altına düşen ilk uçuşta bir kez haber veririz — sürekli takip
                    için rotayı favorilerinize de ekleyebilirsiniz.
                  </p>
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {error}
                  </div>
                )}

                <div className="space-y-3">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full gap-2"
                    size="lg"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Oluşturuluyor...
                      </>
                    ) : (
                      <>
                        <Bell className="w-5 h-5" />
                        Alarmı Oluştur
                      </>
                    )}
                  </Button>

                  <Button
                    type="button"
                    onClick={handleClose}
                    variant="outline"
                    className="w-full"
                    size="lg"
                    disabled={isSubmitting}
                  >
                    İptal
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
